'use client';

import { X } from 'lucide-react';
import * as React from 'react';
import { Button } from './button';
import { cn } from '@core/lib';
import { type VariantProps, cva } from 'class-variance-authority';

const bannerVariants = cva(
  'relative w-full border px-4 py-3 text-sm flex items-center gap-3',
  {
    variants: {
      variant: {
        default:
          'bg-card text-card-foreground border-border [&>svg]:text-foreground',
        destructive:
          'bg-destructive/10 border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive',
        warning:
          'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-950/50 dark:border-yellow-800 dark:text-yellow-200 [&>svg]:text-yellow-600 dark:[&>svg]:text-yellow-400',
        info: 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-950/50 dark:border-blue-800 dark:text-blue-200 [&>svg]:text-blue-600 dark:[&>svg]:text-blue-400',
        success:
          'bg-green-50 border-green-200 text-green-800 dark:bg-green-950/50 dark:border-green-800 dark:text-green-200 [&>svg]:text-green-600 dark:[&>svg]:text-green-400',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const Banner = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof bannerVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role='status'
    aria-live='polite'
    className={cn(bannerVariants({ variant }), className)}
    {...props}
  />
));
Banner.displayName = 'Banner';

const BannerIcon = React.forwardRef<
  SVGSVGElement,
  React.ComponentProps<'svg'> & {
    icon: React.ComponentType<React.ComponentProps<'svg'>>;
  }
>(({ icon: Icon, className, ...props }, ref) => (
  <Icon
    ref={ref}
    className={cn('h-4 w-4 shrink-0', className)}
    {...props}
  />
));
BannerIcon.displayName = 'BannerIcon';

const BannerTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('font-medium leading-5 tracking-tight flex-1', className)}
    {...props}
  />
));
BannerTitle.displayName = 'BannerTitle';

const BannerDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm [&_p]:leading-relaxed', className)}
    {...props}
  />
));
BannerDescription.displayName = 'BannerDescription';

const BannerAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, variant = 'outline', size = 'sm', ...props }, ref) => (
  <Button
    ref={ref}
    variant={variant}
    size={size}
    className={cn('ml-auto h-8 shrink-0', className)}
    {...props}
  />
));
BannerAction.displayName = 'BannerAction';

const BannerClose = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button> & {
    onClose?: () => void;
  }
>(({ className, onClose, ...props }, ref) => (
  <Button
    ref={ref}
    variant='ghost'
    size='icon'
    className={cn(
      'h-6 w-6 rounded-md p-0 shrink-0 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none',
      className,
    )}
    onClick={onClose}
    aria-label='Dismiss notification'
    {...props}
  >
    <X className='h-4 w-4' />
  </Button>
));
BannerClose.displayName = 'BannerClose';

export {
  Banner,
  BannerIcon,
  BannerTitle,
  BannerDescription,
  BannerAction,
  BannerClose,
};
